import api from './api';
import type {
  OrgUnit,
  OrgUnitDetail,
  CreateOrgUnitInput,
  UpdateOrgUnitInput,
  DeleteOrgUnitResult,
  OrgUnitType,
} from '../types/orgUnit';
import type {
  NotificationDeliveriesFilters,
  NotificationDeliveriesListResponse,
} from '../types/notificationDelivery';

export interface AdminUser {
  user_id: string;
  customer_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  phone_number: string | null;
  role: string;
  is_active: boolean;
  is_portal_user: boolean;
  last_login: string | null;
  created_at: string;
  site_access?: { site_id: string; name: string }[] | string[];
  group_memberships?: { org_unit_id: string; name: string; type: OrgUnitType }[];
}

export interface CreateUserData {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  phone_number?: string;
  role: string;
}

export type WelcomeEmailStatus = { sent: true } | { sent: false; error: string };

export interface CreateUserResponse {
  success: true;
  data: AdminUser;
  // Optional because a stale backend (deploy in progress, or rollback) may serve a
  // 201 without the `email` field. Consumers must defensive-check before accessing.
  email?: WelcomeEmailStatus;
}

export interface UpdateUserData {
  first_name?: string;
  last_name?: string;
  role?: string;
  is_portal_user?: boolean;
  company_name?: string;
  phone_number?: string;
}

export async function fetchUsers(params?: { search?: string; role?: string; status?: string; page?: number; limit?: number }) {
  const qs = new URLSearchParams();
  if (params?.search) qs.append('search', params.search);
  if (params?.role) qs.append('role', params.role);
  if (params?.status) qs.append('status', params.status);
  if (params?.page) qs.append('page', params.page.toString());
  if (params?.limit) qs.append('limit', params.limit.toString());
  const res = await api.get(`/v1/admin/users?${qs.toString()}`);
  return res.data;
}

export async function fetchUser(id: string) {
  const res = await api.get(`/v1/admin/users/${id}`);
  return res.data.data as AdminUser;
}

export async function createUser(data: CreateUserData): Promise<CreateUserResponse> {
  const res = await api.post('/v1/admin/users', data);
  return res.data as CreateUserResponse;
}

export async function updateUser(id: string, data: UpdateUserData) {
  const res = await api.put(`/v1/admin/users/${id}`, data);
  return res.data;
}

export async function deactivateUser(id: string) {
  const res = await api.delete(`/v1/admin/users/${id}`);
  return res.data;
}

export async function reactivateUser(id: string) {
  const res = await api.put(`/v1/admin/users/${id}`, { is_active: true });
  return res.data;
}

export async function setUserSiteAccess(id: string, siteIds: string[]) {
  const res = await api.put(`/v1/admin/users/${id}/sites`, { site_ids: siteIds });
  return res.data;
}

export async function setUserGroups(id: string, orgUnitIds: string[]) {
  const res = await api.put(`/v1/admin/users/${id}/groups`, { org_unit_ids: orgUnitIds });
  return res.data.data as { added: string[]; removed: string[] };
}

/**
 * Admin-set temporary password. Prefer `sendResetEmail` instead — this
 * function is kept as a fallback for users with no working email address.
 * The caller supplies the password; do not expose the value in a toast or
 * log (PR #320 removed the old `Temp${random}!` pattern from the UI).
 */
export async function resetPassword(id: string, newPassword: string) {
  const res = await api.post(`/v1/admin/users/${id}/reset-password`, { new_password: newPassword });
  return res.data;
}

/**
 * Admin-triggered self-service reset email. Generates a fresh token for the
 * target user and emails them the standard reset link. Preferred admin
 * password-recovery action (#320).
 *
 * #361: when the backend's email transport is unavailable (SendGrid free-tier
 * exhausted pending #298 Phase 2 ACS migration; or any transient transport
 * failure), the response carries `reset_url` so the admin can deliver the
 * link out-of-band. `email_dispatched: false` + presence of `reset_url`
 * is the signal to render the copyable-link warning banner.
 */
export async function sendResetEmail(id: string): Promise<{
  success: boolean;
  message: string;
  email_dispatched: boolean;
  reset_url?: string;
}> {
  const res = await api.post(`/v1/admin/users/${id}/send-reset-email`);
  return res.data;
}

// ---------------------------------------------------------------------------
// Site Settings (admin-editable metadata CRUD — issue #55)
// ---------------------------------------------------------------------------

export interface SiteSettings {
  site_id: string;
  name: string;
  site_code: string | null;
  timezone: string | null;
  capacity_mwh: number | null;
  commissioned_date: string | null;
  battery_count: number;
  inverter_count: number;
  address: { street?: string; city?: string; state?: string; zip?: string } | null;
  location: { lat: number; lon: number } | null;
  metadata: Record<string, unknown> | null;
  /** Operational status. Only 'maintenance' is operator-settable (djin-cloud#795). */
  status?: 'online' | 'offline' | 'degraded' | 'maintenance' | null;
}

export interface SiteAuditEntry {
  log_id: number;
  actor_id: string;
  actor_email: string;
  action: 'site_update';
  target_type: 'site';
  target_id: string;
  details: {
    changed_fields: string[];
    old_values: Record<string, unknown>;
    new_values: Record<string, unknown>;
  };
  source_ip: string | null;
  created_at: string;
}

export interface SiteAuditLogPage {
  entries: SiteAuditEntry[];
  pagination: { total: number; page: number; limit: number; total_pages: number };
}

/**
 * Raw DB row shape returned by GET /v1/sites/:id. POINT comes back in the
 * node-postgres default parser as { x, y } with x=lon and y=lat.
 */
interface RawSiteRow {
  site_id: string;
  name: string;
  site_code: string | null;
  timezone: string | null;
  capacity_mwh: string | number | null;
  commissioned_date: string | null;
  battery_count: number;
  inverter_count: number;
  address: Record<string, unknown> | null;
  location: { x: number; y: number } | null;
  metadata: Record<string, unknown> | null;
  status?: SiteSettings['status'];
}

function normalizeSite(row: RawSiteRow): SiteSettings {
  const addr = row.address && Object.keys(row.address).length > 0
    ? (row.address as { street?: string; city?: string; state?: string; zip?: string })
    : null;
  return {
    site_id: row.site_id,
    name: row.name,
    site_code: row.site_code,
    timezone: row.timezone,
    capacity_mwh: row.capacity_mwh === null || row.capacity_mwh === undefined
      ? null
      : Number(row.capacity_mwh),
    commissioned_date: row.commissioned_date,
    battery_count: row.battery_count ?? 0,
    inverter_count: row.inverter_count ?? 0,
    address: addr,
    location: row.location ? { lat: row.location.y, lon: row.location.x } : null,
    metadata: row.metadata && Object.keys(row.metadata).length > 0 ? row.metadata : null,
    status: row.status ?? null,
  };
}

export async function fetchSiteSettings(siteId: string): Promise<SiteSettings> {
  const res = await api.get(`/v1/sites/${siteId}`);
  return normalizeSite(res.data as RawSiteRow);
}

export async function updateSiteSettings(
  siteId: string,
  payload: Partial<Omit<SiteSettings, 'site_id' | 'site_code' | 'status'>> & {
    /** 'maintenance' declares operator maintenance; null clears it (djin-cloud#795). */
    status?: 'maintenance' | null;
  }
): Promise<{ success: true; site: RawSiteRow; changed: string[] }> {
  const res = await api.put(`/v1/sites/${siteId}/settings`, payload);
  return res.data;
}

export async function fetchSiteAuditLog(
  siteId: string,
  params: { page?: number; limit?: number } = {}
): Promise<SiteAuditLogPage> {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  const res = await api.get(`/v1/sites/${siteId}/audit-log${qs.toString() ? `?${qs}` : ''}`);
  return res.data.data as SiteAuditLogPage;
}

// ---------------------------------------------------------------------------
// Device Ratings (admin-editable per-device metadata — issue #122)
// ---------------------------------------------------------------------------

export const CHEMISTRY_VALUES = ['LFP', 'NMC', 'NCA', 'lead_acid', 'other'] as const;
export const FUEL_TYPE_VALUES = ['diesel', 'natural_gas', 'biogas', 'hydrogen', 'other'] as const;
export const PHASE_COUNT_VALUES = [1, 3] as const;

export type ChemistryValue = typeof CHEMISTRY_VALUES[number];
export type FuelTypeValue = typeof FUEL_TYPE_VALUES[number];

export interface DeviceWithRatings {
  device_id: string;
  device_type: string;
  display_name: string;
  status: string;
  block_id: string | null;
  // Ratings — all nullable. pg returns NUMERIC as string; normalized to number below.
  power_rating_kw: number | null;
  energy_rating_kwh: number | null;
  chemistry: ChemistryValue | null;
  max_power_kw: number | null;
  fuel_type: FuelTypeValue | null;
  phase_count: number | null;
  dc_rating_kw: number | null;
  ac_rating_kw: number | null;
  ct_ratio_primary: number | null;
  ct_ratio_secondary: number | null;
  voltage_class_v: number | null;
  port_count: number | null;
}

export interface DeviceAuditEntry {
  log_id: number;
  actor_id: string;
  actor_email: string;
  action: 'device_update';
  target_type: 'device';
  target_id: string;
  details: {
    site_id: string;
    device_id: string;
    device_type: string;
    changed_fields: string[];
    old_values: Record<string, unknown>;
    new_values: Record<string, unknown>;
  };
  source_ip: string | null;
  created_at: string;
}

export interface DeviceAuditLogPage {
  entries: DeviceAuditEntry[];
  pagination: { total: number; page: number; limit: number; total_pages: number };
}

// Raw row from GET /v1/sites/:id/devices — NUMERIC columns arrive as strings.
interface RawDeviceRow {
  device_id: string;
  device_type: string;
  display_name: string;
  status: string;
  block_id: string | null;
  power_rating_kw: string | number | null;
  energy_rating_kwh: string | number | null;
  chemistry: string | null;
  max_power_kw: string | number | null;
  fuel_type: string | null;
  phase_count: number | null;
  dc_rating_kw: string | number | null;
  ac_rating_kw: string | number | null;
  ct_ratio_primary: string | number | null;
  ct_ratio_secondary: string | number | null;
  voltage_class_v: string | number | null;
  port_count: number | null;
}

function toNum(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  return typeof v === 'string' ? Number(v) : v;
}

function normalizeDevice(row: RawDeviceRow): DeviceWithRatings {
  return {
    device_id: row.device_id,
    device_type: row.device_type,
    display_name: row.display_name,
    status: row.status,
    block_id: row.block_id,
    power_rating_kw: toNum(row.power_rating_kw),
    energy_rating_kwh: toNum(row.energy_rating_kwh),
    chemistry: (row.chemistry ?? null) as ChemistryValue | null,
    max_power_kw: toNum(row.max_power_kw),
    fuel_type: (row.fuel_type ?? null) as FuelTypeValue | null,
    phase_count: row.phase_count,
    dc_rating_kw: toNum(row.dc_rating_kw),
    ac_rating_kw: toNum(row.ac_rating_kw),
    ct_ratio_primary: toNum(row.ct_ratio_primary),
    ct_ratio_secondary: toNum(row.ct_ratio_secondary),
    voltage_class_v: toNum(row.voltage_class_v),
    port_count: row.port_count,
  };
}

export async function fetchSiteDevicesWithRatings(siteId: string): Promise<DeviceWithRatings[]> {
  const res = await api.get(`/v1/sites/${siteId}/devices`);
  return (res.data as RawDeviceRow[]).map(normalizeDevice);
}

/** Allowlist per device_type — mirrors DEVICE_RATINGS_ALLOWLIST_BY_TYPE on the backend. */
export const DEVICE_RATINGS_FIELDS_BY_TYPE: Record<string, readonly (keyof DeviceWithRatings)[]> = {
  battery_storage: ['power_rating_kw', 'energy_rating_kwh', 'chemistry'],
  pcs:             ['power_rating_kw', 'phase_count'],
  solar_pv:        ['dc_rating_kw', 'ac_rating_kw', 'phase_count'],
  generator:       ['power_rating_kw', 'max_power_kw', 'fuel_type'],
  meter:           ['ct_ratio_primary', 'ct_ratio_secondary', 'voltage_class_v'],
  ev_charger:      ['power_rating_kw', 'port_count'],
  other:           [],
};

export async function updateDeviceRatings(
  siteId: string,
  deviceId: string,
  payload: Record<string, number | string | null>
): Promise<{ success: true; device: RawDeviceRow; changed: string[] }> {
  const res = await api.put(`/v1/sites/${siteId}/devices/${deviceId}/ratings`, payload);
  return res.data;
}

export async function fetchDeviceAuditLog(
  siteId: string,
  deviceId: string,
  params: { page?: number; limit?: number } = {}
): Promise<DeviceAuditLogPage> {
  const qs = new URLSearchParams({ target_type: 'device', target_id: deviceId });
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  const res = await api.get(`/v1/sites/${siteId}/audit-log?${qs.toString()}`);
  return res.data.data as DeviceAuditLogPage;
}

// ---------------------------------------------------------------------------
// Site Contacts (per-site contact directory — issue #64)
// ---------------------------------------------------------------------------

export interface SiteContact {
  contact_id: string;
  site_id: string;
  name: string;
  role_title: string | null;
  phone: string | null;
  email: string | null;
  notes: string | null;
  linked_user_id: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateSiteContactData {
  name: string;
  role_title?: string | null;
  phone?: string | null;
  email?: string | null;
  notes?: string | null;
  sort_order?: number;
}

export type UpdateSiteContactData = Partial<CreateSiteContactData>;

export async function fetchSiteContacts(siteId: string): Promise<SiteContact[]> {
  const res = await api.get(`/v1/sites/${siteId}/contacts`);
  return res.data.data.contacts as SiteContact[];
}

export async function createSiteContact(
  siteId: string,
  data: CreateSiteContactData
): Promise<SiteContact> {
  const res = await api.post(`/v1/sites/${siteId}/contacts`, data);
  return res.data.data as SiteContact;
}

export async function updateSiteContact(
  siteId: string,
  contactId: string,
  data: UpdateSiteContactData
): Promise<SiteContact> {
  const res = await api.put(`/v1/sites/${siteId}/contacts/${contactId}`, data);
  return res.data.data as SiteContact;
}

export async function deleteSiteContact(siteId: string, contactId: string): Promise<void> {
  await api.delete(`/v1/sites/${siteId}/contacts/${contactId}`);
}

// ─────────────────────────────────────────────────────────────────────────
// Org hierarchy — Phase 3a (#53). Backend: /api/v1/admin/org-units*.
// All responses follow the Phase 2 { success, data } envelope; unwrap
// res.data.data explicitly.
// ─────────────────────────────────────────────────────────────────────────

export interface ListOrgUnitsParams {
  type?: OrgUnitType;
  customer_id?: string; // platform_admin only
}

export async function listOrgUnits(params: ListOrgUnitsParams = {}): Promise<OrgUnit[]> {
  const res = await api.get('/v1/admin/org-units', { params });
  return res.data.data.org_units as OrgUnit[];
}

export async function getOrgUnit(orgUnitId: string): Promise<OrgUnitDetail> {
  const res = await api.get(`/v1/admin/org-units/${orgUnitId}`);
  return res.data.data as OrgUnitDetail;
}

export async function createOrgUnit(input: CreateOrgUnitInput): Promise<OrgUnit> {
  const res = await api.post('/v1/admin/org-units', input);
  return res.data.data as OrgUnit;
}

export async function updateOrgUnit(
  orgUnitId: string,
  patch: UpdateOrgUnitInput,
): Promise<{ data: OrgUnit; changed: string[] }> {
  const res = await api.put(`/v1/admin/org-units/${orgUnitId}`, patch);
  return {
    data: res.data.data as OrgUnit,
    changed: (res.data.changed ?? []) as string[],
  };
}

export async function deleteOrgUnit(
  orgUnitId: string,
  cascade: boolean,
): Promise<DeleteOrgUnitResult> {
  const res = await api.delete(`/v1/admin/org-units/${orgUnitId}`, {
    params: cascade ? { cascade: 'true' } : {},
  });
  return res.data.data as DeleteOrgUnitResult;
}

export async function setSiteOrgUnit(
  siteId: string,
  orgUnitId: string | null,
): Promise<void> {
  await api.put(`/v1/admin/sites/${siteId}/org-unit`, { org_unit_id: orgUnitId });
}

// ─────────────────────────────────────────────────────────────────────────
// Notification Deliveries Audit (#265) — GET /api/v1/admin/notification-deliveries.
// platform_admin + parent_org_admin only. Backend returns the standard
// { success, data: { deliveries, total, page, limit } } envelope.
// ─────────────────────────────────────────────────────────────────────────

export async function fetchNotificationDeliveries(
  filters: NotificationDeliveriesFilters = {},
): Promise<NotificationDeliveriesListResponse> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== '') {
      qs.append(k, String(v));
    }
  }
  const res = await api.get(`/v1/admin/notification-deliveries${qs.toString() ? `?${qs}` : ''}`);
  return res.data as NotificationDeliveriesListResponse;
}
